// NOTICE: KAPTION-INTEGRATION
// whatsapp-calling.DESKTOP.4 — noise suppression on the mic track BEFORE it is
// published to the bridge. It lives in the page, not in Electron, because a
// MediaStreamTrack cannot cross Electron's contextBridge: the desktop app can
// only hand the page data (the RNNoise worklet source and its WASM binary),
// and the page runs it in its own AudioContext.
//
//   off      → browser capture with noiseSuppression: false
//   standard → the browser's built-in WebRTC noise suppression (every browser)
//   strong   → RNNoise in an AudioWorklet, when a provider supplies it
//              (rumo-desktop does, via window.rumoDesktop.calls); without a
//              provider it degrades to `standard`, never to `off`.

export const NOISE_LEVELS = ['off', 'standard', 'strong'];

const captureConstraints = (deviceId, browserNoiseSuppression) => ({
  audio: {
    ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
    echoCancellation: true,
    autoGainControl: true,
    noiseSuppression: browserNoiseSuppression,
  },
  video: false,
});

const plain = stream => {
  const [track] = stream.getAudioTracks();
  return {
    stream,
    track,
    level: null,
    dispose: () => stream.getTracks().forEach(t => t.stop()),
  };
};

// provider() → {processorName, workletSource, wasmBinary} (rumo-desktop's
// RNNoise build). Returns null when the AudioWorklet path is unavailable.
const applyWorklet = async (stream, provider, AudioContextImpl) => {
  if (!provider || !AudioContextImpl) return null;
  const assets = await provider();
  if (!assets?.workletSource || !assets?.processorName) return null;

  const context = new AudioContextImpl({ sampleRate: 48000 });
  const moduleUrl = URL.createObjectURL(
    new Blob([assets.workletSource], { type: 'application/javascript' })
  );
  try {
    await context.audioWorklet.addModule(moduleUrl);
  } finally {
    URL.revokeObjectURL(moduleUrl);
  }
  // eslint-disable-next-line no-undef
  const node = new AudioWorkletNode(context, assets.processorName, {
    processorOptions: { wasmBinary: assets.wasmBinary, maxChannels: 1 },
  });
  const source = context.createMediaStreamSource(stream);
  const destination = context.createMediaStreamDestination();
  source.connect(node);
  node.connect(destination);
  const [track] = destination.stream.getAudioTracks();
  return {
    stream: destination.stream,
    track,
    level: 'strong',
    dispose: () => {
      stream.getTracks().forEach(t => t.stop());
      destination.stream.getTracks().forEach(t => t.stop());
      node.disconnect();
      source.disconnect();
      context.close().catch(() => {});
    },
  };
};

export const buildMicStream = async ({
  mediaDevices,
  deviceId = null,
  level = 'standard',
  provider = null,
  AudioContextImpl = window.AudioContext,
}) => {
  if (!mediaDevices?.getUserMedia) {
    throw new Error('[WHATSAPP-CALL] microphone unavailable');
  }

  if (level === 'strong' && provider) {
    // RNNoise does the work; the browser's own suppressor would double-process.
    const raw = await mediaDevices.getUserMedia(
      captureConstraints(deviceId, false)
    );
    try {
      const processed = await applyWorklet(raw, provider, AudioContextImpl);
      if (processed) return processed;
    } catch (e) {
      // fall through to standard below
    }
    raw.getTracks().forEach(t => t.stop());
  }

  const browserNs = level !== 'off';
  const stream = await mediaDevices.getUserMedia(
    captureConstraints(deviceId, browserNs)
  );
  return { ...plain(stream), level: browserNs ? 'standard' : 'off' };
};
